// servizi/social.ts — profilo pubblico (@nome), link personale e richieste di sfida, crediti e negozio, statistiche,
// Wall of Fame. Solo chiamate HTTP: le regole stanno sul server.
import { richiesta } from "./api.ts";
import { idDispositivo } from "./dispositivo.ts";
import { avatarAttuale } from "./account.ts";
import type { SfidaCreata } from "./sfide.ts";
import { indirizzoSito } from "../gioco/Condividi.tsx";

const enc = encodeURIComponent;

export interface ProfiloPubblico {
  nome: string;
  nomeUtente: string;
  accetta: boolean;
  /** v020 (null o assente con un server vecchio = nessuno) */
  avatar?: number | null;
  /** v033: amicizia con chi guarda (null = ospite o il mio profilo) */
  amicizia?: "nessuna" | "inviata" | "ricevuta" | "amici" | null;
  /** v039: solo per chi guarda con l'account (non il proprio profilo): id e se l'ho bloccato */
  id?: number;
  bloccato?: boolean;
}
export const leggiProfiloPubblico = (nome: string) => richiesta<ProfiloPubblico>(`/api/v1/profili/${enc(nome)}`);

export function sfidaProfilo(nomeUtente: string, mioNome: string): Promise<SfidaCreata & { per: { nome: string; nomeUtente: string } }> {
  return richiesta(`/api/v1/profili/${enc(nomeUtente)}/sfida`, { metodo: "POST", corpo: { nome: mioNome, dispositivo: idDispositivo(), avatar: avatarAttuale() } });
}

export interface RichiestaSfida {
  id: number;
  nome: string;
  creataIl: string;
  scadeIl: string;
}
export const leggiRichieste = () => richiesta<{ richieste: RichiestaSfida[] }>("/api/v1/richieste").then((r) => r.richieste);
/** v033: le richieste e il numero delle richieste di amicizia (lo stesso controllo dice al server che l'app è aperta). */
export const leggiRichiesteEAmici = () =>
  richiesta<{ richieste: RichiestaSfida[]; amiciRicevute?: number }>("/api/v1/richieste").then((r) => ({ richieste: r.richieste, amiciRicevute: r.amiciRicevute ?? 0 }));
export const accettaRichiesta = (id: number) =>
  richiesta<{ codice: string; token: string }>(`/api/v1/richieste/${id}/accetta`, { metodo: "POST", corpo: { dispositivo: idDispositivo() } });
export const rifiutaRichiesta = (id: number, blocca: boolean) => richiesta<void>(`/api/v1/richieste/${id}/rifiuta`, { metodo: "POST", corpo: { blocca } });

export const impostaNomeUtente = (nomeUtente: string) =>
  richiesta<{ nomeUtente: string; link: string }>("/api/v1/io/nome-utente", { metodo: "PUT", corpo: { nomeUtente } });
export const impostaLinkAttivo = (attivo: boolean) => richiesta<{ attivo: boolean }>("/api/v1/io/link", { metodo: "PUT", corpo: { attivo } });
export const impostaClassifica = (inClassifica: boolean, paese: string | null) =>
  richiesta<{ inClassifica: boolean; paese: string | null; paeseBloccato: boolean }>("/api/v1/io/classifica", {
    metodo: "PUT",
    corpo: { inClassifica, paese },
  });

export interface Statistiche {
  giocate: number;
  vinte: number;
  perse: number;
  valide: number;
  valideVinte: number;
  resistenzaMassimaMs: number;
  /** v037: persone portate su Laughdown */
  portati?: number;
}
export const leggiStatistiche = () => richiesta<Statistiche>("/api/v1/io/statistiche");

export interface MovimentoCrediti {
  delta: number;
  motivo: "apertura" | "partita_valida" | "registrato" | "partite" | "condivisione" | "giorno" | "acquisto" | "amministratore";
  stato: "confermato" | "sospetto" | "tetto" | "annullato";
  il: string;
}
export const leggiCrediti = () => richiesta<{ saldo: number; movimenti: MovimentoCrediti[] }>("/api/v1/io/crediti");
/** v030: come si guadagnano le stelle (lo Store le elenca nel menu del prezzo). */
export interface RegoleStelle {
  attivi: boolean;
  amicoNuovo: number;
  primaPartitaAmico: number;
  amicoGiocatore: number;
  amicoAvversari: number;
  partiteOgni: number;
  partitePremio: number;
  tettoGiorno: number;
  /** v037 */
  giorno?: number;
  condivisione?: number;
}
export const leggiRegoleStelle = () => richiesta<RegoleStelle>("/api/v1/crediti/regole");
export const compraFiltro = (filtro: string) => richiesta<{ saldo: number; filtro: string }>(`/api/v1/negozio/${enc(filtro)}/compra`, { metodo: "POST" });

export interface RigaClassifica {
  posizione: number;
  nome: string;
  nomeUtente: string | null;
  paese: string | null;
  avatar?: number | null;
  vinte: number;
  giocate: number;
}
export function leggiClassifica(periodo: "sempre" | "mese", paese: string | null): Promise<RigaClassifica[]> {
  const q = new URLSearchParams({ periodo });
  if (paese) q.set("paese", paese);
  return richiesta<{ righe: RigaClassifica[]; aggiornataIl?: string | null; ogniMinuti?: number }>(`/api/v1/classifica?${q.toString()}`).then((r) => {
    ultimaClassifica = { aggiornataIl: r.aggiornataIl ?? null, ogniMinuti: r.ogniMinuti ?? null };
    return r.righe;
  });
}
/** v041: quando il worker ha calcolato l'ultima classifica letta, e ogni quanti minuti la ricalcola. */
export let ultimaClassifica: { aggiornataIl: string | null; ogniMinuti: number | null } = { aggiornataIl: null, ogniMinuti: null };

/** Link personale da condividere. */
export const linkPersonale = (nomeUtente: string): string => `${indirizzoSito()}/@${nomeUtente}`;

/** Codici dei paesi (ISO 3166-1 alpha-2) per la scelta del paese: i nomi li dà Intl.DisplayNames nella lingua dell'app. */
export const PAESI =
  "AD AE AF AG AL AM AO AR AT AU AZ BA BB BD BE BF BG BH BI BJ BN BO BR BS BT BW BY BZ CA CD CF CG CH CI CL CM CN CO CR CU CV CY CZ DE DJ DK DM DO DZ EC EE EG ER ES ET FI FJ FR GA GB GD GE GH GM GN GQ GR GT GW GY HK HN HR HT HU ID IE IL IN IQ IR IS IT JM JO JP KE KG KH KM KN KR KW KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MG MK ML MM MN MO MR MT MU MV MW MX MY MZ NA NE NG NI NL NO NP NZ OM PA PE PG PH PK PL PS PT PY QA RO RS RU RW SA SB SC SD SE SG SI SK SL SM SN SO SR SS ST SV SY SZ TD TG TH TJ TL TM TN TO TR TT TW TZ UA UG US UY UZ VA VC VE VN VU WS XK YE ZA ZM ZW".split(" ");
